import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Chip,
  Divider,
  Link as MuiLink,
  CircularProgress,
  Button,
  Stack,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  LinearProgress,
  IconButton,
} from '@mui/material';

import {
  LocationOnOutlined,
  PhoneOutlined,
  MailOutline,
  TimerOutlined,
  Verified,
  SettingsOutlined,
  ShareOutlined,
  CalendarMonth, // Importation de l'icône de calendrier
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios'; // Assurez-vous qu'axios est bien importé
import { toast } from 'react-toastify'; // Utile pour afficher les messages de succès/erreur

const Profile = () => {
  const { user, profile, fetchProfile } = useAuth();
  const [isActionLoading, setIsActionLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  // 🔥 sync profile
  useEffect(() => {
    if (user?.id && !profile) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 🔗 Connexion Google Calendar
  const handleGoogleConnect = async () => {
    try {
      setIsActionLoading(true);
      const response = await axios.get(`${apiUrl}/intervenant/google-calendar/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.url) {
        window.location.href = response.data.url; // Redirection vers Google
      } else {
        toast.error("Impossible de récupérer le lien de connexion.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la tentative de connexion à Google Calendar");
    } finally {
      setIsActionLoading(false);
    }
  };

 const handleGoogleDisconnect = async () => {
  if (!window.confirm("Êtes-vous sûr de vouloir déconnecter votre compte Google Calendar ?")) return;

  try {
    setIsActionLoading(true);

    const response = await axios.delete(
      `${apiUrl}/intervenant/google-calendar/disconnect`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const message = response?.data?.message;

    if (response?.data?.status === 'success') {
      toast.success(message || "Déconnexion réussie");
      fetchProfile();
    } else {
      toast.error(message || "Déconnexion échouée");
    }

  } catch (err) {
    console.error(err);

    const message =
      err?.response?.data?.message ||
      "Erreur lors de la déconnexion";

    toast.error(message);
  } finally {
    setIsActionLoading(false);
  }
};
  // ⛔ loading state sécurisé
  if (!user || !profile) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: '#000',
        }}
      >
        <CircularProgress thickness={2} sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  const p = profile;
  const sessions = p.user_sessions || [];

  const totalMin = sessions.reduce(
    (acc, curr) => acc + (curr.working_minutes || 0),
    0
  );

  const cardStyle = {
    p: 3,
    borderRadius: '24px',
    bgcolor: '#fff',
    border: '1px solid #f1f5f9',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  };

  return (
    <Box sx={{ bgcolor: '#fbfcfd', minHeight: '100vh', pb: 10 }}>
      
      {/* HEADER */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #f1f5f9', pt: 6, pb: 4, mb: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">

            <Grid item>
              <Avatar
                src={`${process.env.REACT_APP_API_URL}/storage/workshop/${p.photo_user}`}
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  bgcolor: '#6366f1',
                  fontSize: '3rem',
                  border: '4px solid #fff',
                  boxShadow: '0 15px 35px -5px rgba(99, 102, 241, 0.3)',
                }}
              >
                {p.nom_user?.charAt(0)}
              </Avatar>
            </Grid>

            <Grid item xs>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h4" fontWeight="900">
                  {p.prenom_user} {p.nom_user}
                </Typography>
                <Verified sx={{ color: '#6366f1' }} />
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography sx={{ color: '#6366f1', fontWeight: 700 }}>
                  {p.title_user}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography sx={{ color: '#64748b' }}>
                  ID: {p.id_user}
                </Typography>
              </Stack>
            </Grid>

            <Grid item>
              <Stack direction="row" spacing={2}>
                <IconButton>
                  <ShareOutlined />
                </IconButton>
                <IconButton>
                  <SettingsOutlined />
                </IconButton>
              </Stack>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* BODY */}
      <Container maxWidth="lg">
        <Grid container spacing={3}>

          {/* BIO */}
          <Grid item xs={12} md={8}>
            <Paper sx={cardStyle}>
              <Typography variant="h6" fontWeight="800" sx={{ mb: 2 }}>
                Expertise & Bio
              </Typography>

              <Typography sx={{ color: '#475569', lineHeight: 1.7 }}>
                {p.description_user}
              </Typography>

              <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {p.specialities?.map((s) => (
                  <Chip key={s.id} label={s.specialty} />
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* STATS */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...cardStyle, bgcolor: '#0f172a', color: '#fff' }}>
              <Typography sx={{ color: '#94a3b8' }}>Performance</Typography>

              <Typography variant="h2" fontWeight="900">
                {p.nbr_view}
              </Typography>

              <Typography sx={{ color: '#94a3b8' }}>
                Vues profil
              </Typography>

              <Divider sx={{ my: 2, borderColor: '#1e293b' }} />

              <Typography>
                Commission: {p.commission}%
              </Typography>

              <LinearProgress
                variant="determinate"
                value={Number(p.commission)}
                sx={{
                  mt: 2,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#1e293b',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#6366f1',
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* SESSIONS */}
          <Grid item xs={12} md={8}>
            <Paper sx={cardStyle}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6" fontWeight="800">
                  Activité récente
                </Typography>

                <Typography sx={{ bgcolor: '#f1f5f9', px: 1.5, borderRadius: 1 }}>
                  {totalMin} min
                </Typography>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {sessions.slice(0, 5).map((s) => (
                      <TableRow key={s.id_userSession}>
                        
                        <TableCell>
                          {new Date(s.login_time).toLocaleDateString('fr-FR')}
                        </TableCell>

                        <TableCell>
                          <TimerOutlined sx={{ fontSize: 16, mr: 1 }} />
                          {s.working_minutes} min
                        </TableCell>

                        <TableCell align="right">
                          {s.logout_time ? (
                            <Chip label="Terminé" size="small" />
                          ) : (
                            <Chip label="En cours" color="primary" size="small" />
                          )}
                        </TableCell>

                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* GOOGLE CALENDAR & CONTACT BLOCK */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3} sx={{ height: '100%' }}>
              
              {/* GOOGLE CALENDAR CARD */}
              {Number(p.role_user) === 3 && (
                <Paper sx={cardStyle}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <CalendarMonth sx={{ color: '#6366f1' }} />
                    <Typography variant="h6" fontWeight="800">
                      Google Calendar
                    </Typography>
                  </Stack>

                  {p.google_calendar_account && p.google_calendar_account.is_connected ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip 
                          label="Synchronisé" 
                          color="success" 
                          size="small" 
                          sx={{ fontWeight: 'bold', mb: 1.5 }} 
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                          Compte connecté :
                        </Typography>
                        <Typography variant="body1" fontWeight="700" sx={{ wordBreak: 'break-all' }}>
                          {p.google_calendar_account.email}
                        </Typography>
                      </Box>
                      
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        size="small"
                        disabled={isActionLoading}
                        onClick={handleGoogleDisconnect}
                        sx={{ borderRadius: '12px', textTransform: 'none' }}
                      >
                        {isActionLoading ? <CircularProgress size={20} /> : "Déconnecter mon calendrier"}
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Associez votre calendrier Google pour synchroniser automatiquement vos disponibilités et rendez-vous sur la plateforme.
                      </Typography>
                      
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={isActionLoading}
                        onClick={handleGoogleConnect}
                        sx={{ 
                          bgcolor: '#6366f1', 
                          borderRadius: '12px', 
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#4f46e5' }
                        }}
                      >
                        {isActionLoading ? <CircularProgress size={20} color="inherit" /> : "Connecter Google Calendar"}
                      </Button>
                    </Box>
                  )}
                </Paper>
              )}

              {/* CONTACT CARD */}
              <Paper sx={cardStyle}>
                <Typography variant="h6" fontWeight="800" sx={{ mb: 2 }}>
                  Contact
                </Typography>

                <Stack spacing={2}>
                  <ContactRow icon={<MailOutline />} text={p.email_user} label="Email" />
                  <ContactRow icon={<PhoneOutlined />} text={p.phone_user} label="Phone" />
                  <ContactRow icon={<LocationOnOutlined />} text={p.ville_user} label="Ville" />
                </Stack>

                <Button
                  component={MuiLink}
                  href={p.lien_boutique}
                  target="_blank"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Boutique
                </Button>
              </Paper>

            </Stack>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

// =======================
// CONTACT ROW
// =======================
const ContactRow = ({ icon, text, label }) => (
  <Stack direction="row" spacing={2} alignItems="center">
    {icon}
    <Box>
      <Typography variant="caption">{label}</Typography>
      <Typography fontWeight="700">{text}</Typography>
    </Box>
  </Stack>
);

export default Profile;